<template>
  <div class="seasonEndContainer">
    <div v-if="modalView === SeasonModalView.end && position && !BigNumber(position).isZero()" class="statisticWrapper">
      <div class="statisticItem">
        <span class="label">{{ $t('placeInSeason') }}:</span>
        <span class="value">
          <img alt="" height="45" src="/img/season_cup.svg" width="45" />
          {{ positionInSeasonMulticallData?.[seasonToCheckRewards]?.[0] }}
        </span>
      </div>

      <div class="statisticItem">
        <span class="label">{{ $t('seasonPoints') }}:</span>
        <span class="value">
          <img :src="tokens.seasonPoints" alt="" height="45" width="45" />
          {{ getUserScoreMulticallData?.[seasonToCheckRewards]?.[1] }}
        </span>
      </div>
    </div>
    <button v-if="modalView === SeasonModalView.end" class="btn-primary" @click="openHandler">
      {{ $t('continue') }}!
    </button>

    <el-carousel
      v-if="modalView === SeasonModalView.rewards"
      :autoplay="false"
      :class="{
        'season-end-carousel-mobile': $device.isMobile
      }"
      :height="carouselHeight"
      :initial-index="BigNumber(currentSeasonId!).toNumber()"
      :loop="false"
      arrow="always"
      indicator-position="none"
      @change="onSeasonSlideChange"
    >
      <el-icon-arrow-left
        v-if="seasonToCheckRewards === 0"
        :style="{ top: $device.isMobile ? '130px' : '50%' }"
        style="position: absolute; opacity: 0.6; left: 16px; transform: translateY(-50%) scaleY(3)"
        width="28"
      />
      <el-icon-arrow-right
        v-if="seasonToCheckRewards === BigNumber(currentSeasonId!).minus(1).toNumber()"
        :style="{ top: $device.isMobile ? '130px' : '50%' }"
        style="position: absolute; opacity: 0.6; right: 16px; transform: translateY(-50%) scaleY(3)"
        width="28"
      />

      <el-skeleton
        :loading="!currentSeasonId || !getUserSeasonPositionRewardsMulticallData?.[seasonToCheckRewards]"
        animated
      >
        <template #template>
          <el-carousel-item
            :style="{
              height: $device.isMobile ? 'fit-content' : 'unset'
            }"
          >
            <el-skeleton-item style="width: 100%; height: 280px" />
          </el-carousel-item>
        </template>
        <template #default>
          <el-carousel-item
            v-for="item in BigNumber(currentSeasonId!).toNumber() || 0"
            :key="item"
            :style="{
              height: $device.isMobile ? 'fit-content' : undefined
            }"
          >
            <div class="carouselItem">
              <div class="statisticWrapper">
                <div :class="{ row: !$device.isMobile }" class="statisticItem">
                  <span class="label">{{ $t('placeInSeason') }}:</span>
                  <span class="value">
                    <img alt="" height="45" src="/img/season_cup.svg" width="45" />
                    {{ positionInSeasonMulticallData?.[seasonToCheckRewards]?.[0] }}
                  </span>
                </div>

                <div :class="{ row: !$device.isMobile }" class="statisticItem">
                  <span class="label">{{ $t('seasonPoints') }}:</span>
                  <span class="value">
                    <img :src="tokens.seasonPoints" alt="" height="45" width="45" />
                    {{ getUserScoreMulticallData?.[seasonToCheckRewards]?.[1] }}
                  </span>
                </div>
              </div>
              <h5
                v-if="!$device.isMobile && getUserSeasonPositionRewardsMulticallData?.[seasonToCheckRewards][0]?.length"
                class="rewardsHeading"
              >
                {{ $t('yourRewards') }}
              </h5>
              <div
                v-if="
                  getUserSeasonPositionRewardsMulticallData?.[seasonToCheckRewards][0]?.length &&
                  !claimedSeasons.has(seasonToCheckRewards)
                "
                :class="{
                  'direction-column':
                    getUserSeasonPositionRewardsMulticallData?.[seasonToCheckRewards].length > 3 || $device.isMobile
                }"
                class="claimBlock"
              >
                <div
                  v-for="reward of getUserSeasonPositionRewardsMulticallData?.[seasonToCheckRewards][0]"
                  :key="reward[0]"
                  class="rewardTokens"
                >
                  <div class="rewardToken">
                    <img
                      :src="tokens[tokenMetaData(blockchain.contracts, reward[0])?.name?.contract as Tokens] as string"
                      alt=""
                      width="40"
                    />
                    {{ !reward[2] ? BigNumber(formatEther(reward[1])).toFormat(2) : BigNumber(reward[1]).toFormat(2) }}
                  </div>
                </div>
                <button
                  v-if="address"
                  :disabled="isLoadingClaimButton || claimedSeasons.has(seasonToCheckRewards)"
                  class="btn-primary btn-primary_season-design"
                  type="button"
                  @click="claimReward"
                >
                  <span>{{ $t('appStatisticClaim') }}</span>
                  &nbsp;<span v-if="isLoadingClaimButton"></span>

                  <img alt="chevron right" src="/img/icons/chevronRight.svg" />
                </button>
              </div>
              <button
                v-if="$device.isMobile"
                class="btn-outline w-button small continueBtn"
                @click="() => emit('close')"
              >
                {{ $t('continue') }}
              </button>
            </div>
          </el-carousel-item>
        </template>
      </el-skeleton>
    </el-carousel>
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#app';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import { Multicall__factory, Season__factory, SeasonRewardsYear1__factory } from 'public/abi/evm/types';
import tokens from '~/public/img/tokens';
import { SeasonModalView } from '~/types/season';
import type { Tokens } from '~/utils/constants';
import { nonNullable } from '~/utils/non-nullable';

const { getProvider } = useProvider();
const { blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();

const { sendContractMethod } = useSendContractMethod();
const store = useMainStore();
const { notificationUpdateVersion } = storeToRefs(store);
const { t } = useI18n();
const { isMobile } = useDevice();
const { getSigner } = useProvider();

const config = useRuntimeConfig();

const year2Indexes = config.public.env === 'prod' ? 41 : 1006;
const selectYearContract = (index: number) => {
  if (index >= year2Indexes) return blockchain.contracts.SEASON_REWARDS_YEAR_2;
  return blockchain.contracts.SEASON_REWARDS_YEAR_1;
};

const position = inject<Ref<number, number>>('playerPosition');
const modalView = inject('modalView');
const showSeasonModal = inject('showSeasonModal');
const currentSeasonId = inject<Ref<number>>('currentSeasonId');

const seasonContractInterface = Season__factory.createInterface();
const seasonYearContractInterface = SeasonRewardsYear1__factory.createInterface();

const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
const seasonContract = Season__factory.connect(blockchain.contracts.season, getProvider());

const seasonsCount = await seasonContract?.getSeasonsLength();

const calldata = computed(() =>
  nonNullable(
    Array(Number(seasonsCount))
      .fill(null)
      .map((_, index) => {
        return {
          getUserPositionInSeason: [
            blockchain.contracts.season,
            seasonContractInterface.encodeFunctionData('getUserPositionInSeason', [index, String(address.value), 1000])
          ] as const,
          getUserSeasonPositionRewards: [
            selectYearContract(index),
            seasonYearContractInterface.encodeFunctionData('getUserSeasonPositionRewards', [
              index,
              String(address.value)
            ])
          ] as const,
          getUserScore: [
            blockchain.contracts.season,
            seasonContractInterface.encodeFunctionData('getUserScore', [index, String(address.value)])
          ] as const
        };
      })
  )
);

const carouselHeight = computed(() => {
  if (!isMobile) {
    return 'auto';
  }

  if (isMobile && getUserSeasonPositionRewardsMulticallData.value?.[seasonToCheckRewards.value][0]?.length) {
    return 'auto';
  } else {
    return '300px';
  }
});

const positionInSeasonMulticallData = asyncComputed(
  async () =>
    await multicallContract?.tryAggregate
      .staticCall(
        true,
        nonNullable(
          calldata.value.map((item) => {
            if (BigNumber(notificationUpdateVersion.value).isNaN()) return null;
            return {
              target: item.getUserPositionInSeason[0],
              callData: item.getUserPositionInSeason[1] || '0x'
            };
          })
        )
      )
      .then((res: any) =>
        res.map((item: any) => {
          return seasonContractInterface.decodeFunctionResult('getUserPositionInSeason', item[1]);
        })
      )
);

const getUserSeasonPositionRewardsMulticallData = asyncComputed(
  async () =>
    await multicallContract?.tryAggregate
      .staticCall(
        true,
        nonNullable(
          calldata.value.map((item) => {
            if (BigNumber(notificationUpdateVersion.value).isNaN()) return null;
            return {
              target: item.getUserSeasonPositionRewards[0],
              callData: item.getUserSeasonPositionRewards[1] || '0x'
            };
          })
        )
      )
      .then((res: any) =>
        res.map((item: any) => {
          return seasonYearContractInterface.decodeFunctionResult('getUserSeasonPositionRewards', item[1]);
        })
      )
);

const getUserScoreMulticallData = asyncComputed(
  async () =>
    await multicallContract?.tryAggregate
      .staticCall(
        true,
        nonNullable(
          calldata.value.map((item) => {
            if (BigNumber(notificationUpdateVersion.value).isNaN()) return null;

            return {
              target: item.getUserScore[0],
              callData: item.getUserScore[1]
            };
          })
        )
      )
      .then((res: any) =>
        res.map((item: any) => {
          return seasonContractInterface.decodeFunctionResult('getUserScore', item[1]);
        })
      )
);

const seasonToCheckRewards = ref(BigNumber(currentSeasonId?.value!).minus(1).toNumber());
const isLoadingClaimButton = ref(false);
const claimedSeasons = ref<Set<number>>(new Set());

watch(
  showSeasonModal!,
  (newValue) => {
    if (!newValue) {
      seasonToCheckRewards.value = BigNumber(currentSeasonId?.value!).minus(1).toNumber();
      claimedSeasons.value.clear();
    }
  },
  { immediate: true }
);

onBeforeMount(() => {
  emit('updateCheckedSeason', BigNumber(currentSeasonId?.value!).minus(1).toNumber());
});

const emit = defineEmits<{
  close: [];
  updateCheckedSeason: [value: number];
  updateModalView: [value: SeasonModalView];
}>();

watch(
  seasonToCheckRewards,
  (newSeason) => {
    if (!BigNumber(newSeason).isNaN()) emit('updateCheckedSeason', newSeason);
  },
  { immediate: true }
);

const openHandler = () => {
  emit('updateModalView', SeasonModalView.rewards);
};

const onSeasonSlideChange = (newI: number) => {
  seasonToCheckRewards.value = newI;
};

const claimReward = async () => {
  isLoadingClaimButton.value = true;
  const seasonToClaim = seasonToCheckRewards.value;

  try {
    const claimTx = await sendContractMethod({
      contract: SeasonRewardsYear1__factory.connect(selectYearContract(seasonToClaim || 0), await getSigner()),
      methodName: 'claimSeasonRewards',
      args: [BigNumber(seasonToClaim || 0).toNumber()]
    });

    if (!claimTx?.hash) {
      isLoadingClaimButton.value = false;
      return;
    }

    saveHashToLocalStorage(t('seasonRewards'), claimTx.hash);
    store.updateVersion();

    await claimTx?.wait();

    modifyHistoryHashStatus(claimTx.hash, 'Done');
    store.updateVersion();

    claimedSeasons.value.add(seasonToClaim);
  } catch (error) {
    console.error(error, 'error on claim');
    ElNotification.error({
      title: '',
      message: 'Error while claiming crafted token'
    });
  } finally {
    isLoadingClaimButton.value = false;
  }
};
</script>

<style lang="scss" scoped>
.seasonEndContainer {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  flex-direction: column;
  gap: 25px;

  .statisticWrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 67px;
    @media (max-width: 767px) {
      flex-direction: column;
      gap: 10px;
    }

    .statisticItem {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      font-weight: bold;
      font-family: Grenze, sans-serif;
      &.row {
        flex-direction: row;
        gap: 10px;
        .value {
          font-size: 24px;
        }
      }

      .label {
        font-size: 20px;
        color: white;
      }

      .value {
        font-size: 40px;
        color: #eea92e;
        display: flex;
        align-items: center;
      }
    }
  }

  .claimBlock {
    width: 80%;

    @media (max-width: 767px) {
      width: 100%;
    }

    height: fit-content;
    display: flex;
    justify-content: center;
    gap: 10px;
    align-items: center;
    border: 2px solid #1e353e;
    padding: 15px 10px;
    border-radius: 12px;
    flex-wrap: wrap;

    &.direction-column {
      gap: 20px;
      flex-direction: column;
      flex-wrap: wrap;
    }

    background: linear-gradient(180deg, #60ff7c 0%, #04202b 100%);
  }

  .rewardTokens {
    display: flex;
    gap: 10px;
    align-items: center;
    @media (max-width: 767px) {
      flex-wrap: wrap;
    }
  }

  .rewardToken {
    display: flex;
    align-items: center;
    color: white;
    font-size: 24px;
    font-weight: 800;
  }

  .carouselItem {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    min-height: 200px;
    align-items: center;

    @media (max-width: 767px) {
      justify-content: flex-start;
    }
  }

  .rewardsHeading {
    margin-top: 30px;
    font-size: 24px;
    font-weight: 500;
    width: 100%;
    text-align: center;
  }

  .btn-primary_season-design {
    padding: 12px 24px;
    display: flex;
    gap: 8px;
    font-size: 18px;
    text-wrap: nowrap;
    flex-shrink: 0;
  }

  .continueBtn {
    width: 80%;
    margin-top: 20px;
    margin-bottom: 20px;
    min-height: 48px;
  }
}
</style>

<style lang="scss">
.el-dialog.seasonModal {
  .el-carousel {
    width: 100%;
  }

  .el-carousel__item {
    overflow-y: scroll;
  }

  .season-end-carousel-mobile {
    .el-carousel__arrow {
      top: 25px;
    }
  }

  .el-carousel__arrow {
    @media (max-width: 767px) {
      top: 130px;
    }

    &:hover {
      background-color: transparent;
    }

    .el-icon {
      width: 100%;
      height: 100%;

      svg {
        transform: scaleX(2) scaleY(3);
      }
    }
    background-color: transparent;
    width: 28px;
    height: 78px;
  }
}
</style>
